import { Contract, ContractGroup } from "@/lib/types";

export type SortDirection = "asc" | "desc" | null;

export type SortableColumn = 
  | "supplierName" 
  | "importance" 
  | "type" 
  | "category" 
  | "expiryDate" 
  | "signedDate" 
  | "expectedCost" 
  | "expiryStatus"
  | "group";

export interface FilterState {
  importance: number[] | null;
  type: string[] | null;
  category: string[] | null;
  expiryDateRange: { start: Date | null; end: Date | null } | null;
  expiryStatus: string[] | null;
  groups: string[] | null; // Added groups filter
}

export type BadgeVariant = "outline" | "default" | "destructive" | "secondary" | "warning" | "success";

export const importanceBadges = {
  1: { label: "Very Low", className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300", variant: "outline" as BadgeVariant },
  2: { label: "Low", className: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300", variant: "outline" as BadgeVariant },
  3: { label: "Medium", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300", variant: "outline" as BadgeVariant },
  4: { label: "High", className: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300", variant: "outline" as BadgeVariant },
  5: { label: "Critical", className: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300", variant: "outline" as BadgeVariant },
};

export const EXPIRY_STATUS_OPTIONS = [
  "Expired",
  "Expiring Soon",
  "Auto-Renewing",
  "Active"
];

export const getDaysUntilExpiry = (expiryDate: Date): number => {
  const now = new Date();
  const diff = expiryDate.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 3600 * 24));
};

export const getExpiryStatusText = (contract: any): string => {
  const daysUntilExpiry = getDaysUntilExpiry(contract.expiryDate);
  
  // Check if contract has an auto-renewing contract type
  const isAutoRenewing = contract.type?.auto_renews === true;

  if (daysUntilExpiry < 0) {
    return isAutoRenewing ? "Auto-Renewing" : "Expired";
  } else if (daysUntilExpiry <= 30) {
    return isAutoRenewing ? "Auto-Renewing" : "Expiring Soon";
  } else {
    return "Active";
  }
};

export const getExpiryStatusColor = (daysUntilExpiry: number, isAutoRenewing: boolean): string => {
  if (isAutoRenewing) {
    return "#3b82f6"; // blue for auto-renewing contracts
  }
  
  if (daysUntilExpiry < 0) {
    return "#ef4444"; // red for expired
  } else if (daysUntilExpiry <= 7) {
    return "#f97316"; // orange for very soon
  } else if (daysUntilExpiry <= 30) {
    return "#f59e0b"; // amber for expiring soon
  } else {
    return "#10b981"; // green for active
  }
};
